import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { cssMap } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';

import { AISnippetWithRelayEnvironment } from '@atlassian/ai-snippet/ai-snippet';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';

import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { PageCardContext } from '../PageCardContext.ts';
import {
	AutomationDiscoveryMessage,
	ContentIcon,
	LastAction,
	PageCardContainer,
	PageLink,
	SocialProof,
	Subtitle,
	Title,
	AudioPlaylistButton,
} from '../Items/index.ts';

const styles = xcss({
	width: '400px',
});
const headerStyle = xcss({
	width: '100%',
	overflowWrap: 'break-word',
	wordBreak: 'break-all',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
const headerIconStyle = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignSelf: 'flex-start',
});

const titleStyles = xcss({
	font: 'font.heading.medium',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	overflowWrap: 'break-word',
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
	textAlign: 'start',
	WebkitBoxPack: 'start',
});

const aiSnippetStyles = cssMap({
	container: {
		minHeight: '96px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		pre: {
			// We need this so code wraps in the preview, without it the code will overflow the card
			whiteSpace: 'pre-wrap',
		},
		textAlign: 'start',
	},
});

type PopupViewWithAISnippetProps = {
	shouldUseAutomationDiscovery?: boolean; // this should probably go into PageTreeContext, however since this is the only usage, it's ok to leave it here for now
	error?: boolean;
	snippet?: string;
	isEndOfPage?: boolean;
	contentId: string;
	contentType: string;
	shouldUseAISnippetPlatformComponent?: boolean;
	onLoadComplete?: () => void;
	onCardClick?: () => void;
};

export const PopupViewWithAISnippet: FC<PopupViewWithAISnippetProps> = ({
	shouldUseAutomationDiscovery,
	snippet,
	isEndOfPage = false,
	contentId,
	contentType,
	onLoadComplete,
	shouldUseAISnippetPlatformComponent = false,
	onCardClick,
}) => {
	const { cloudId, userId } = useSessionData();
	const { excerpt } = useContext(PageCardContext);
	const { trackAIResultAction } = useAIEventsInstrumentation();

	const handlePopupClick = useCallback(() => {
		if (snippet || shouldUseAISnippetPlatformComponent) {
			trackAIResultAction('aiSnippetCardClicked', {
				attributes: {
					source: isEndOfPage ? 'endOfPageRecommendations' : 'pageTree',
				},
			});
		}
	}, [trackAIResultAction, isEndOfPage, snippet, shouldUseAISnippetPlatformComponent]);

	return (
		<PageCardContainer isHoverPageCard onClick={handlePopupClick}>
			<PageLink
				shouldHideFolder
				featureAttributionCode={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
				onCardClick={onCardClick}
			/>
			<Box padding="space.200" xcss={styles} testId="page-card-popup-view">
				<Stack alignBlock="start" alignInline="start" grow="fill" space="space.200">
					<Inline
						alignBlock="center"
						alignInline="start"
						shouldWrap={false}
						grow="fill"
						space="space.100"
					>
						<Box xcss={headerIconStyle} as="span">
							<ContentIcon />
						</Box>
						<Stack xcss={headerStyle} alignBlock="start" alignInline="start" grow="fill">
							<Box xcss={titleStyles} as="span">
								<Title />
							</Box>
							<Subtitle />
						</Stack>
					</Inline>
					{excerpt && (
						<Box xcss={aiSnippetStyles.container}>
							<AISnippetWithRelayEnvironment
								contentId={contentId}
								contentType={contentType}
								cloudId={cloudId}
								source={isEndOfPage ? 'endOfPageRecommendations' : 'pageTree'}
								onLoadComplete={onLoadComplete}
								fallbackText={excerpt}
								from={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
								userId={userId ?? undefined}
							/>
						</Box>
					)}
					<Inline
						alignBlock="center"
						spread="space-between"
						shouldWrap={false}
						grow="fill"
						space="space.200"
					>
						<LastAction />
						<Inline alignBlock="center" space="space.100">
							<SocialProof />
							<AudioPlaylistButton isEndOfPage={isEndOfPage} />
						</Inline>
					</Inline>
				</Stack>
			</Box>
			<AutomationDiscoveryMessage shouldUseAutomationDiscovery={shouldUseAutomationDiscovery} />
		</PageCardContainer>
	);
};
